import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const CATEGORIES  = ['Computer & IT', 'Furniture', 'Vehicle', 'Machinery', 'Office Equipment', 'Building', 'Land', 'Other'];
const STATUSES    = ['in_service', 'under_maintenance', 'disposed', 'idle'];
const DEP_METHODS = [
  { value: 'straight_line',      label: 'Straight Line' },
  { value: 'declining_balance',  label: 'Declining Balance' },
];
const MAINT_TYPES = ['routine', 'repair', 'inspection', 'upgrade', 'other'];

const ASSET_INIT = {
  name: '', code: '', category: '', purchase_date: new Date().toISOString().slice(0, 10),
  purchase_cost: '', salvage_value: '', depreciation_method: 'straight_line',
  useful_life_years: '5', location: '', assigned_to: '', status: 'in_service', notes: '',
};
const MAINT_INIT = {
  date: new Date().toISOString().slice(0, 10),
  type: 'routine', cost: '', description: '', next_due_date: '',
};

const LIMIT = 15;

function fmtMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function statusBadge(s) {
  const map = { in_service: 'success', under_maintenance: 'warning', disposed: 'danger', idle: 'secondary' };
  const label = s ? s.replace('_', ' ') : s;
  return <span className={`badge badge-${map[s] || 'secondary'}`}>{label}</span>;
}
function depPct(asset) {
  const cost = parseFloat(asset.purchase_cost) || 0;
  const cur  = parseFloat(asset.current_value)  || 0;
  if (!cost) return 0;
  return Math.round(((cost - cur) / cost) * 100);
}

export default function Assets({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [statusF, setStatusF]   = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [employees, setEmployees] = useState([]);

  // Asset modal
  const [assetModal, setAssetModal] = useState(false);
  const [form, setForm]   = useState(ASSET_INIT);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Depreciation modal (for a specific asset)
  const [depAsset, setDepAsset]     = useState(null);
  const [depModal, setDepModal]     = useState(false);
  const [depHistory, setDepHistory] = useState([]);
  const [depMonth, setDepMonth]     = useState(new Date().getMonth() + 1);
  const [depYear, setDepYear]       = useState(new Date().getFullYear());
  const [depRunning, setDepRunning] = useState(false);

  // Maintenance modal (for a specific asset)
  const [maintAsset, setMaintAsset] = useState(null);
  const [maintModal, setMaintModal] = useState(false);
  const [maintList, setMaintList]   = useState([]);
  const [maintForm, setMaintForm]   = useState(MAINT_INIT);
  const [maintSaving, setMaintSaving] = useState(false);

  useEffect(() => { setTitle('Asset Management'); }, [setTitle]);

  useEffect(() => {
    api.get('/employees/all').then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/assets?search=${encodeURIComponent(search)}&status=${statusF}&category=${encodeURIComponent(category)}&page=${page}&limit=${LIMIT}`
      );
      setItems(data.data || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [search, statusF, category, page]);

  useEffect(() => { load(); }, [load]);

  // ── Asset CRUD ──────────────────────────────────────────────────────────────
  const openAdd  = () => { setForm(ASSET_INIT); setEditId(null); setAssetModal(true); };
  const openEdit = item => {
    setForm({
      name: item.name, code: item.code || '', category: item.category || '',
      purchase_date: item.purchase_date ? item.purchase_date.slice(0, 10) : '',
      purchase_cost: item.purchase_cost ?? '', current_value: item.current_value ?? '',
      salvage_value: item.salvage_value ?? '',
      depreciation_method: item.depreciation_method || 'straight_line',
      useful_life_years: item.useful_life_years ?? '5',
      location: item.location || '', assigned_to: item.assigned_to || '',
      status: item.status || 'in_service', notes: item.notes || '',
    });
    setEditId(item.id); setAssetModal(true);
  };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Asset name is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/assets/${editId}`, form);
      else        await api.post('/assets', form);
      setAssetModal(false); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving asset');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete asset "${name}"? This will also delete its depreciation and maintenance records.`)) return;
    try { await api.delete(`/assets/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  // ── Depreciation ────────────────────────────────────────────────────────────
  const openDepModal = async item => {
    setDepAsset(item); setDepModal(true); setDepRunning(false);
    try {
      const { data } = await api.get(`/assets/${item.id}`);
      setDepHistory(data.depreciation || []);
    } catch { setDepHistory([]); }
  };

  const runDepreciation = async () => {
    if (!depAsset) return;
    setDepRunning(true);
    try {
      const { data } = await api.post(`/assets/${depAsset.id}/depreciate`, { month: depMonth, year: depYear });
      alert(`Depreciation posted: ${fmtMoney(data.depreciation_amount)}\nNew book value: ${fmtMoney(data.book_value_after)}`);
      setDepAsset(data.asset);
      const { data: d } = await api.get(`/assets/${depAsset.id}`);
      setDepHistory(d.depreciation || []);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error running depreciation');
    } finally { setDepRunning(false); }
  };

  // ── Maintenance ─────────────────────────────────────────────────────────────
  const openMaintModal = async item => {
    setMaintAsset(item); setMaintModal(true); setMaintForm(MAINT_INIT);
    try {
      const { data } = await api.get(`/assets/${item.id}/maintenance`);
      setMaintList(data || []);
    } catch { setMaintList([]); }
  };

  const mf = (k, v) => setMaintForm(p => ({ ...p, [k]: v }));

  const handleAddMaint = async () => {
    if (!maintForm.date) return alert('Date is required');
    setMaintSaving(true);
    try {
      await api.post(`/assets/${maintAsset.id}/maintenance`, maintForm);
      const { data } = await api.get(`/assets/${maintAsset.id}/maintenance`);
      setMaintList(data || []);
      setMaintForm(MAINT_INIT);
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving maintenance');
    } finally { setMaintSaving(false); }
  };

  const handleDelMaint = async mid => {
    if (!window.confirm('Delete this maintenance record?')) return;
    try {
      await api.delete(`/assets/${maintAsset.id}/maintenance/${mid}`);
      setMaintList(prev => prev.filter(m => m.id !== mid));
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const months = [
    {v:1,l:'January'},{v:2,l:'February'},{v:3,l:'March'},{v:4,l:'April'},
    {v:5,l:'May'},{v:6,l:'June'},{v:7,l:'July'},{v:8,l:'August'},
    {v:9,l:'September'},{v:10,l:'October'},{v:11,l:'November'},{v:12,l:'December'},
  ];
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Asset Management</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Asset
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Assets',     value: summary.count },
            { label: 'Total Cost',       value: fmtMoney(summary.total_cost) },
            { label: 'Current Value',    value: fmtMoney(summary.total_value) },
            { label: 'In Service',       value: summary.in_service },
          ].map(s => (
            <div className="stat-card" key={s.label}>
              <div className="label">{s.label}</div>
              <div className="value" style={{ fontSize: 20 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <input
            className="form-control" style={{ width: 200 }}
            placeholder="Search name, code, location…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <select className="form-control" style={{ width: 160 }} value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select className="form-control" style={{ width: 180 }} value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} assets</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Category</th>
                  <th>Purchase Cost</th>
                  <th>Current Value</th>
                  <th>Depreciated</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No assets found</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name}</strong>
                      {item.code && <div className="text-muted" style={{ fontSize: 11 }}>Code: {item.code}</div>}
                      {item.assigned_name && <div className="text-muted" style={{ fontSize: 11 }}>Assigned: {item.assigned_name}</div>}
                    </td>
                    <td>{item.category || '—'}</td>
                    <td className="text-right">{fmtMoney(item.purchase_cost)}</td>
                    <td className="text-right"><strong>{fmtMoney(item.current_value)}</strong></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, background: '#e9ecef', borderRadius: 4, height: 6 }}>
                          <div style={{ width: `${depPct(item)}%`, background: '#4f46e5', borderRadius: 4, height: 6 }} />
                        </div>
                        <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{depPct(item)}%</span>
                      </div>
                    </td>
                    <td className="text-muted">{item.location || '—'}</td>
                    <td>{statusBadge(item.status)}</td>
                    <td>
                      <div className="actions">
                        {/* Depreciate */}
                        <button className="btn-icon" title="Depreciation Schedule" onClick={() => openDepModal(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </button>
                        {/* Maintenance */}
                        <button className="btn-icon" title="Maintenance Log" onClick={() => openMaintModal(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                        </button>
                        {/* Edit */}
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {/* Delete */}
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Add / Edit Asset Modal ── */}
      {assetModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAssetModal(false)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h2>{editId ? 'Edit Asset' : 'Add Asset'}</h2>
              <button className="modal-close" onClick={() => setAssetModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Asset Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Dell Laptop #3" />
                </div>
                <div className="form-group">
                  <label>Asset Code</label>
                  <input className="form-control" value={form.code} onChange={e => f('code', e.target.value)} placeholder="e.g. IT-001" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select className="form-control" value={form.category} onChange={e => f('category', e.target.value)}>
                    <option value="">Select category…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Purchase Date</label>
                  <input type="date" className="form-control" value={form.purchase_date} onChange={e => f('purchase_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Purchase Cost</label>
                  <input type="number" className="form-control" value={form.purchase_cost} onChange={e => f('purchase_cost', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                {editId && (
                  <div className="form-group">
                    <label>Current Value</label>
                    <input type="number" className="form-control" value={form.current_value} onChange={e => f('current_value', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                  </div>
                )}
                <div className="form-group">
                  <label>Salvage Value</label>
                  <input type="number" className="form-control" value={form.salvage_value} onChange={e => f('salvage_value', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Depreciation Method</label>
                  <select className="form-control" value={form.depreciation_method} onChange={e => f('depreciation_method', e.target.value)}>
                    {DEP_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Useful Life (years)</label>
                  <input type="number" className="form-control" value={form.useful_life_years} onChange={e => f('useful_life_years', e.target.value)} placeholder="5" step="0.5" min="0.5" />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input className="form-control" value={form.location} onChange={e => f('location', e.target.value)} placeholder="e.g. Head Office – Room 2" />
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
                    <option value="">Unassigned</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAssetModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Asset' : 'Add Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Depreciation Modal ── */}
      {depModal && depAsset && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDepModal(false)}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>Depreciation — {depAsset.name}</h2>
              <button className="modal-close" onClick={() => setDepModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                <div className="stat-card" style={{ flex: 1, minWidth: 120 }}>
                  <div className="label">Purchase Cost</div>
                  <div className="value" style={{ fontSize: 16 }}>{fmtMoney(depAsset.purchase_cost)}</div>
                </div>
                <div className="stat-card" style={{ flex: 1, minWidth: 120 }}>
                  <div className="label">Current Value</div>
                  <div className="value" style={{ fontSize: 16 }}>{fmtMoney(depAsset.current_value)}</div>
                </div>
                <div className="stat-card" style={{ flex: 1, minWidth: 120 }}>
                  <div className="label">Salvage Value</div>
                  <div className="value" style={{ fontSize: 16 }}>{fmtMoney(depAsset.salvage_value)}</div>
                </div>
              </div>

              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Method: <strong>{depAsset.depreciation_method?.replace('_', ' ')}</strong> &nbsp;|&nbsp;
                Useful Life: <strong>{depAsset.useful_life_years} years</strong>
              </p>

              {/* Run depreciation */}
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>Run Monthly Depreciation</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 130 }}>
                    <label>Month</label>
                    <select className="form-control" value={depMonth} onChange={e => setDepMonth(parseInt(e.target.value))}>
                      {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 100 }}>
                    <label>Year</label>
                    <select className="form-control" value={depYear} onChange={e => setDepYear(parseInt(e.target.value))}>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={runDepreciation} disabled={depRunning || depAsset.status === 'disposed'}>
                    {depRunning ? 'Running…' : 'Run Depreciation'}
                  </button>
                </div>
                {depAsset.status === 'disposed' && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>Cannot depreciate a disposed asset.</p>}
              </div>

              {/* Depreciation history */}
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Depreciation History</div>
              <div className="table-wrap" style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Book Value After</th>
                      <th>Posted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depHistory.length === 0 ? (
                      <tr><td colSpan={4} className="empty">No depreciation records yet</td></tr>
                    ) : depHistory.map(d => (
                      <tr key={d.id}>
                        <td>{months.find(m => m.v === d.month)?.l} {d.year}</td>
                        <td className="text-right">{fmtMoney(d.amount)}</td>
                        <td className="text-right"><strong>{fmtMoney(d.book_value_after)}</strong></td>
                        <td className="text-muted" style={{ fontSize: 11 }}>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDepModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Maintenance Modal ── */}
      {maintModal && maintAsset && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMaintModal(false)}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <h2>Maintenance Log — {maintAsset.name}</h2>
              <button className="modal-close" onClick={() => setMaintModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              {/* Add maintenance form */}
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>Add Maintenance Record</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Date *</label>
                    <input type="date" className="form-control" value={maintForm.date} onChange={e => mf('date', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-control" value={maintForm.type} onChange={e => mf('type', e.target.value)}>
                      {MAINT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Cost</label>
                    <input type="number" className="form-control" value={maintForm.cost} onChange={e => mf('cost', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                  </div>
                  <div className="form-group">
                    <label>Next Due Date</label>
                    <input type="date" className="form-control" value={maintForm.next_due_date} onChange={e => mf('next_due_date', e.target.value)} />
                  </div>
                  <div className="form-group full">
                    <label>Description</label>
                    <textarea className="form-control" rows={2} value={maintForm.description} onChange={e => mf('description', e.target.value)} placeholder="What was done…" />
                  </div>
                </div>
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button className="btn btn-primary" onClick={handleAddMaint} disabled={maintSaving}>
                    {maintSaving ? 'Saving…' : 'Add Record'}
                  </button>
                </div>
              </div>

              {/* Maintenance history */}
              <div style={{ fontWeight: 600, marginBottom: 8 }}>History</div>
              <div className="table-wrap" style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th className="text-right">Cost</th>
                      <th>Next Due</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintList.length === 0 ? (
                      <tr><td colSpan={6} className="empty">No maintenance records yet</td></tr>
                    ) : maintList.map(m => (
                      <tr key={m.id}>
                        <td className="text-muted">{m.date ? m.date.slice(0, 10) : ''}</td>
                        <td><span className="badge badge-secondary">{m.type}</span></td>
                        <td>{m.description || '—'}</td>
                        <td className="text-right">{fmtMoney(m.cost)}</td>
                        <td className="text-muted">{m.next_due_date ? m.next_due_date.slice(0, 10) : '—'}</td>
                        <td>
                          <button className="btn-icon danger" title="Delete" onClick={() => handleDelMaint(m.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setMaintModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
